import {WarrantyDocumentType} from '../../../../../../../services/hookService/getLoanRequestRule/types';

import {IBankDocsGeneralForm, IPersonFormData} from '../../LoanDocsPage';

export type PersonPrefix = 'self' | 'Guarantor-0' | 'Guarantor-1';

// helper
const isFilled = (value: any) =>
  value !== null && value !== undefined && value !== '';

// cheque config type
interface IChequeConfig {
  sayadiField:
    | 'first_sayadi_cheque_id'
    | 'second_sayadi_cheque_id'
    | 'third_sayadi_cheque_id';

  digitalField:
    | 'first_digital_cheque_id'
    | 'second_digital_cheque_id'
    | 'third_digital_cheque_id';

  imageField: 'first_check_image' | 'second_check_image' | 'third_check_image';
}

// dynamic cheque configs
const CHEQUE_CONFIGS: IChequeConfig[] = [
  {
    sayadiField: 'first_sayadi_cheque_id',

    digitalField: 'first_digital_cheque_id',

    imageField: 'first_check_image',
  },

  {
    sayadiField: 'second_sayadi_cheque_id',

    digitalField: 'second_digital_cheque_id',

    imageField: 'second_check_image',
  },

  {
    sayadiField: 'third_sayadi_cheque_id',

    digitalField: 'third_digital_cheque_id',

    imageField: 'third_check_image',
  },
];

// check if paper cheque complete
const isPaperChequeComplete = (data: IPersonFormData): boolean => {
  const filledChequeCount = CHEQUE_CONFIGS.filter(config => {
    const sayadiValue = data?.[config.sayadiField];

    const imageValue = data?.[config.imageField];

    return isFilled(sayadiValue) && isFilled(imageValue);
  }).length;

  return filledChequeCount > 0;
};

// check if digital cheque complete
const isDigitalChequeComplete = (data: IPersonFormData): boolean => {
  const filledChequeCount = CHEQUE_CONFIGS.filter(config =>
    isFilled(data?.[config.digitalField]),
  ).length;

  return filledChequeCount > 0;
};

// person complete
export const isPersonComplete = (
  data: IPersonFormData,
  prefix: PersonPrefix,
  shouldValidateJobDocs: boolean = false,
): boolean => {
  if (!data) {
    return false;
  }

  // cheque validation
  let chequeComplete = false;

  if (data.checkType === 'paper') {
    chequeComplete = isPaperChequeComplete(data);
  }

  if (data.checkType === 'digital') {
    chequeComplete = isDigitalChequeComplete(data);
  }

  // job docs validation
  const jobDocsComplete = shouldValidateJobDocs
    ? !data.job_type || data._allJobImagesUploaded
    : true;

  // base validation
  const baseComplete = chequeComplete && jobDocsComplete;

  // guarantor validation
  if (prefix !== 'self') {
    return (
      baseComplete && isFilled(data.name) && isFilled(data.cellphone_number)
    );
  }

  return baseComplete;
};

// formik wrapper
export const checkPersonComplete = (
  values: IBankDocsGeneralForm,
  prefix: PersonPrefix,
): boolean => {
  return isPersonComplete(values[prefix], prefix);
};

// all persons complete
export const areAllPersonsComplete = (
  values: IBankDocsGeneralForm,
  warrantyType: WarrantyDocumentType,
): boolean => {
  if (!warrantyType) {
    return false;
  }

  switch (warrantyType) {
    case WarrantyDocumentType.OWN_CHECK:
      return checkPersonComplete(values, 'self');

    case WarrantyDocumentType.ONE_GUARANTOR:
      return checkPersonComplete(values, 'Guarantor-0');

    case WarrantyDocumentType.TWO_GUARANTORS:
      return (
        checkPersonComplete(values, 'Guarantor-0') &&
        checkPersonComplete(values, 'Guarantor-1')
      );

    case WarrantyDocumentType.OWN_CHECK_SINGLE_GUARANTOR:
      return (
        checkPersonComplete(values, 'self') &&
        checkPersonComplete(values, 'Guarantor-0')
      );

    case WarrantyDocumentType.OWN_CHECK_TWO_GUARANTORS:
      return (
        checkPersonComplete(values, 'self') &&
        checkPersonComplete(values, 'Guarantor-0') &&
        checkPersonComplete(values, 'Guarantor-1')
      );

    default:
      return false;
  }
};

// empty person data
export const createEmptyPersonData = (
  loanRequest: string = '',
  loanRule: string = '',
): IPersonFormData => ({
  // guarantor fields
  name: '',

  cellphone_number: '',

  national_code: '',

  role: 0,

  // job
  job_type: 0,

  _allJobImagesUploaded: false,

  // cheque type
  checkType: '',

  // paper cheques
  first_check_image: null,

  second_check_image: null,

  third_check_image: null,

  first_sayadi_cheque_id: '',

  second_sayadi_cheque_id: '',

  third_sayadi_cheque_id: '',

  // digital cheques
  first_digital_cheque_id: '',

  second_digital_cheque_id: '',

  third_digital_cheque_id: '',

  // status
  is_check_registered: false,

  // internal
  loan_request: loanRequest,

  loan_rule: loanRule,
});
